<template>
  <div>
    <a-card style="margin: 20px" title="订单详情">
      <template #extra>
        <a-button @click="goBack">返回</a-button>
      </template>

      <a-spin :spinning="loading">
        <div class="detailBox">
          <!-- 未知结果待确认提示（doc §8.5/§10.4：处理中超10分钟） -->
          <a-alert
              v-if="unknownPending"
              message="结果确认中"
              description="该订单处理已超过10分钟，可能存在结果未知的银行交易。系统不会自动重发，将自动核实结果并更新订单状态；如需紧急处理，请人工核对银行流水。"
              show-icon
              style="margin-bottom: 16px;"
              type="warning"
          />
          <!-- 订单基本信息 -->
          <a-descriptions :column="3" bordered size="small">
            <a-descriptions-item label="订单号">{{ order.orderNo || '-' }}</a-descriptions-item>
            <a-descriptions-item label="商户协议编号">{{ order.merchantNo || '-' }}</a-descriptions-item>
            <a-descriptions-item label="商户协议名称">{{ order.merchantName || '-' }}</a-descriptions-item>
            <a-descriptions-item label="付款方">
              <WalletInfo v-if="order.payerAccount" :account-name="order.payerName" :account-no="order.payerAccount" :show-account-name="true"/>
              <span v-else>-</span>
            </a-descriptions-item>
            <a-descriptions-item label="订单总金额">
              <Balance :amount="order.totalAmount" prefix="￥"/>
            </a-descriptions-item>
            <a-descriptions-item label="订单状态">
              <a-popover v-if="order.failReason" title="失败原因">
                <template #content>{{ order.failReason }}</template>
                <a-tag :color="getDictColor(orderStatusDict, order.status)">{{ getDictLabel(orderStatusDict, order.status) }}</a-tag>
              </a-popover>
              <a-tag v-else :color="getDictColor(orderStatusDict, order.status)">{{ getDictLabel(orderStatusDict, order.status) }}</a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="支付类型">{{ getDictLabel(payAgrDict.payType, order.payType) }}</a-descriptions-item>
            <a-descriptions-item label="支付摘要">{{ order.paySummary || '-' }}</a-descriptions-item>
            <a-descriptions-item label="备注">{{ order.remark || '-' }}</a-descriptions-item>
            <a-descriptions-item label="支付时间">{{ order.payTime || '-' }}</a-descriptions-item>
            <a-descriptions-item label="执行开始时间">{{ order.executeStartTime || '-' }}</a-descriptions-item>
            <a-descriptions-item label="执行完成时间">{{ order.executeEndTime || '-' }}</a-descriptions-item>
            <a-descriptions-item label="创建时间">{{ order.createTime || '-' }}</a-descriptions-item>
            <a-descriptions-item label="创建人">{{ order.creater || '-' }}</a-descriptions-item>
            <a-descriptions-item label="失败原因">{{ order.failReason || '-' }}</a-descriptions-item>
          </a-descriptions>

          <!-- 操作按钮 -->
          <div class="actionBar">
            <a-button
                v-if="$authority('payAgrOrderExecute') && (order.status === 0 || order.status === 1)"
                icon="thunderbolt"
                type="primary"
                @click="triggerExecuteBtn"
            >执行
            </a-button>
            <a-button
                v-if="$authority('payAgrOrderRetry') && order.status === 4"
                :loading="execLoading"
                icon="sync"
                type="primary"
                @click="retryBtn"
            >重试服务费
            </a-button>
          </div>

          <!-- 执行记录 -->
          <a-divider>执行记录（付款在前，服务费按规则顺序）</a-divider>
          <a-table
              :columns="recordColumns"
              :data-source="records"
              :pagination="false"
              :row-key="record => record.id"
              size="small"
              :scroll="{x: 1300}"
          >
            <template slot="taskType" slot-scope="text">
              <a-tag :color="getDictColor(payAgrDict.taskType, text)">{{ getDictLabel(payAgrDict.taskType, text) }}</a-tag>
            </template>
            <template slot="agreementType" slot-scope="text">
              {{ getDictLabel(payAgrDict.agreementType, text) }}
            </template>
            <template slot="payee" slot-scope="text, record">
              <WalletInfo :account-name="record.payeeName" :account-no="text" :show-account-name="true"/>
            </template>
            <template slot="shareAmount" slot-scope="text">
              <Balance :amount="text" prefix="￥"/>
            </template>
            <template slot="status" slot-scope="text, record">
              <a-popover v-if="record.failReason" title="失败原因">
                <template #content>{{ record.failReason }}</template>
                <a-tag :color="getDictColor(payAgrDict.recordStatus, text)">{{ getDictLabel(payAgrDict.recordStatus, text) }}</a-tag>
              </a-popover>
              <a-tag v-else :color="getDictColor(payAgrDict.recordStatus, text)">{{ getDictLabel(payAgrDict.recordStatus, text) }}</a-tag>
            </template>
            <template slot="retry" slot-scope="text, record">
              {{ (record.retryCount || 0) + ' / ' + (record.maxRetry || 0) }}
            </template>
          </a-table>

          <!-- 配置快照 -->
          <a-divider>商户协议信息（下单时确定）</a-divider>
          <pre class="snapshotBox">{{ configSnapshotPretty }}</pre>
        </div>
      </a-spin>
    </a-card>

    <!-- 短信验证码弹窗（详情页执行订单时使用） -->
    <a-modal
        v-model="smsModalVisible"
        title="短信验证并执行"
        :maskClosable="false"
        okText="确认执行"
        cancelText="取消"
        :confirmLoading="smsExecLoading"
        @ok="smsModalExecute"
        @cancel="smsModalCancel"
    >
      <p style="margin-bottom: 12px; color: #666;">
        订单 <strong>{{ order.orderNo || '-' }}</strong>（{{ order.totalAmount || '-' }} 元），
        验证码将发送至付款方预留手机。
      </p>
      <PaymentSms
          v-if="smsConfig && smsConfig.payerMobile"
          :verify-code.sync="verifyCode"
          :account-name="smsConfig.payerName"
          :account-no="smsConfig.payerAccount"
          :channel-no="smsConfig.channelNo"
          :app-no="smsConfig.appNo"
          :pay-amount="order.totalAmount || 0"
          model-no="102216"
          :mobile="smsConfig.payerMobile"
          style="width: 300px;"
      />
      <a-alert
          v-else
          type="warning"
          show-icon
          message="付款方账户未登记手机号，无法发送验证码，请联系管理员补充。"
      />
    </a-modal>
  </div>
</template>

<script>
import {message} from "ant-design-vue";
import WalletInfo from "@/view/components/WalletInfo.vue";
import Balance from "@/view/components/Balance.vue";
import PaymentSms from "@/view/components/PaymentSms.vue";
import {payAgrDict, getDictLabel, getDictColor} from "@/utils/bizDict/payAgrDict";

const orderStatusDict = {...payAgrDict.orderStatus};
delete orderStatusDict.default;

export default {
  name: 'PayAgrOrderDetail',
  components: {WalletInfo, Balance, PaymentSms},
  data() {
    return {
      payAgrDict,
      orderStatusDict,
      loading: false,
      execLoading: false,
      order: {},
      records: [],
      // SMS 验证码弹窗
      smsModalVisible: false,
      smsConfig: null,
      verifyCode: '',
      smsExecLoading: false,
      recordColumns: [
        {title: '任务类型', dataIndex: 'taskType', align: 'center', width: 90, scopedSlots: {customRender: 'taskType'}},
        {title: '协议类型', dataIndex: 'agreementType', align: 'center', width: 90, scopedSlots: {customRender: 'agreementType'}},
        {title: '收款账户', dataIndex: 'payeeAccount', align: 'center', width: 170, scopedSlots: {customRender: 'payee'}},
        {title: '金额', dataIndex: 'shareAmount', align: 'center', width: 110, scopedSlots: {customRender: 'shareAmount'}},
        {title: '状态', dataIndex: 'status', align: 'center', width: 130, scopedSlots: {customRender: 'status'}},
        {title: '重试次数', key: 'retry', align: 'center', width: 90, scopedSlots: {customRender: 'retry'}},
        {title: '银行流水号', dataIndex: 'requestSerialNo', align: 'center', width: 180, customRender: (text) => text || '-'},
        {title: '摘要', dataIndex: 'feeSummary', align: 'center', width: 140, customRender: (text) => text || '-'},
        {title: '执行时间', dataIndex: 'executeTime', align: 'center', width: 150, customRender: (text) => text || '-'},
        {title: '失败原因', dataIndex: 'failReason', align: 'center', customRender: (text) => text || '-'},
      ],
    };
  },
  computed: {
    unknownPending() {
      // 处理中(2) 且执行开始时间距今超过10分钟 → 展示"结果确认中"（doc §10.4）
      if (this.order.status !== 2 || !this.order.executeStartTime) return false;
      const start = new Date(String(this.order.executeStartTime).replace(/-/g, '/'));
      if (Number.isNaN(start.getTime())) return false;
      return Date.now() - start.getTime() > 10 * 60 * 1000;
    },
    configSnapshotPretty() {
      if (!this.order.configSnapshot) return '-';
      try {
        return JSON.stringify(JSON.parse(this.order.configSnapshot), null, 2);
      } catch (e) {
        return this.order.configSnapshot;
      }
    },
  },
  mounted() {
    const orderNo = this.$route.query.orderNo;
    if (orderNo) {
      this.loadDetail(orderNo);
    } else {
      message.warning('缺少订单号参数');
    }
  },
  methods: {
    getDictLabel,
    getDictColor,

    async loadDetail(orderNo) {
      this.loading = true;
      try {
        const res = await this.api.getPayAgrOrderDetail(orderNo);
        if (res.code === 200 && res.data) {
          this.order = res.data.order || {};
          this.records = res.data.records || [];
        } else {
          message.warning(res.info || '获取订单详情失败');
        }
      } catch (error) {
        console.error('获取订单详情失败:', error);
        message.error('获取订单详情失败');
      } finally {
        this.loading = false;
      }
    },

    async triggerExecuteBtn() {
      // 从 configSnapshot 取 channelNo/appNo，手机号实时查询 CustAccountInfo
      let snapshot = {};
      try {
        snapshot = this.order.configSnapshot ? JSON.parse(this.order.configSnapshot) : {};
      } catch (e) {
        console.warn('configSnapshot 解析失败:', e);
      }
      const channelNo = snapshot.channelNo || this.order.channelNo || '';
      let payerMobile = '';
      try {
        const res = await this.api.accountqueryBalancedApi({
          channel_no: channelNo,
          account_no: this.order.payerAccount,
        });
        if (res.code === 200 && res.data) {
          payerMobile = res.data.mobile || '';
        }
      } catch (e) {
        console.error('查询付款方手机号失败:', e);
      }
      this.smsConfig = {
        payerAccount: this.order.payerAccount,
        payerName: this.order.payerName,
        channelNo: channelNo,
        appNo: snapshot.appNo || this.order.appNo || '',
        payerMobile: payerMobile,
      };
      this.verifyCode = '';
      this.smsModalVisible = true;
    },

    async smsModalExecute() {
      if (!this.verifyCode || this.verifyCode.length < 4) {
        message.warning('请输入短信验证码');
        return;
      }
      this.smsExecLoading = true;
      try {
        const res = await this.api.triggerPayAgrExecute({
          orderNo: this.order.orderNo,
          verifyCode: this.verifyCode,
        });
        if (res.code === 200 && res.data) {
          this.smsModalVisible = false;
          const status = res.data.status;
          if (status === 3) {
            message.success('订单执行完成');
          } else if (status === 4) {
            message.warning('存在服务费失败，可继续重试');
          } else if (status === 5) {
            message.error('付款失败，订单已终止');
          } else if (status === 2) {
            message.warning('存在处理中的记录，系统不会自动重发，请稍后核实结果');
          }
          this.loadDetail(this.order.orderNo);
        } else {
          message.warning(res.info || '执行失败');
        }
      } catch (error) {
        console.error('执行失败:', error);
        message.error('执行失败');
      } finally {
        this.smsExecLoading = false;
      }
    },

    smsModalCancel() {
      this.smsModalVisible = false;
    },

    retryBtn() {
      this.$confirm({
        title: '重试服务费',
        content: '确认重新收取失败的服务费？不会重发订单付款。',
        onOk: () => this.doExecute(this.api.retryPayAgrServiceFee),
      });
    },

    async doExecute(apiFn) {
      this.execLoading = true;
      try {
        const res = await apiFn({orderNo: this.order.orderNo});
        if (res.code === 200 && res.data) {
          const status = res.data.status;
          if (status === 3) {
            message.success('订单执行完成');
          } else if (status === 4) {
            message.warning('存在服务费失败，可继续重试');
          } else if (status === 5) {
            message.error('付款失败，订单已终止');
          } else if (status === 2) {
            message.warning('存在处理中的记录，系统不会自动重发，请稍后核实结果');
          }
          this.loadDetail(this.order.orderNo);
        } else {
          message.warning(res.info || '执行失败');
        }
      } catch (error) {
        console.error('执行失败:', error);
        message.error('执行失败');
      } finally {
        this.execLoading = false;
      }
    },

    goBack() {
      this.$router.push('/pay-agr/order');
    },
  },
};
</script>

<style lang="less" scoped>
.detailBox {
  padding: 10px;
}

.actionBar {
  margin: 16px 0;

  .ant-btn {
    margin-right: 8px;
  }
}

.snapshotBox {
  max-height: 320px;
  overflow: auto;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.6;
}
</style>
